import { ReactNode, createElement, useCallback } from 'react';

import { CssVarsProvider } from '@mui/joy/styles';
import { flushSync } from 'react-dom';
import { Root, createRoot } from 'react-dom/client';

import { HomeOnboardingPopover } from '../../components/onboarding/popover/HomeOnboardingPopover';
import { theme } from '../../theme';
import { brand } from '../../theme/brand';
import { track } from '../analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_closed,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../analytics/events';
import { driver } from '../driverjs';

export type OnboardingStep = {
  element: string;
  side: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  title: string;
  body: string;
  show: boolean;
  customContent?: ReactNode;
  interactive?: boolean;
};

export const hasVisibleOnboardingSteps = (steps: OnboardingStep[]): boolean =>
  steps.some((step) => step.show);

// Tutorial analytics (analytics_sheet.pdf). The home tour shows on the
// dashboard, and per the sheet feature_name is the tutorial-level subject
// ("Dashboard"), constant across screens. tutorial_step_number tells screens
// apart by position, and tutorial_screen_header_text = "the first few words of
// the header copy" (the stable per-screen identifier, since step numbers can
// vary per user).
const TUTORIAL_PLATFORM_PAGE = 'dashboard';
const TUTORIAL_FEATURE_NAME = 'dashboard';
const tourHeaderText = (title?: string): string => (title ?? '').split(' ').slice(0, 3).join(' ');

const TOUR_THEME_CSS_ID = 'driverjs-onboarding-theme-style';
const injectTourThemeStyle = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TOUR_THEME_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = TOUR_THEME_CSS_ID;
  style.textContent = `
    .driver-fade .driver-popover.driverjs-onboarding-theme { animation: none !important; }
    .driverjs-onboarding-theme .driver-popover-arrow-side-left  { border-left-color:   var(--joy-palette-background-surface, #ffffff); }
    .driverjs-onboarding-theme .driver-popover-arrow-side-right { border-right-color:  var(--joy-palette-background-surface, #ffffff); }
    .driverjs-onboarding-theme .driver-popover-arrow-side-top   { border-top-color:    var(--joy-palette-background-surface, #ffffff); }
    .driverjs-onboarding-theme .driver-popover-arrow-side-bottom{ border-bottom-color: var(--joy-palette-background-surface, #ffffff); }
    .calyx-tour-dot {
      position: absolute;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #127C16;
      pointer-events: none;
      animation: calyxTourPulse 1.4s ease-out infinite;
    }
    .driver-popover-arrow-side-bottom .calyx-tour-dot {
      top: -28px;
      left: 50%;
      transform: translateX(-50%);
    }
    .driver-popover-arrow-side-top .calyx-tour-dot {
      bottom: -28px;
      left: 50%;
      transform: translateX(-50%);
    }
    .driver-popover-arrow-side-left .calyx-tour-dot {
      right: -28px;
      top: 50%;
      transform: translateY(-50%);
    }
    .driver-popover-arrow-side-right .calyx-tour-dot {
      left: -28px;
      top: 50%;
      transform: translateY(-50%);
    }
    @keyframes calyxTourPulse {
      0%   { box-shadow: 0 0 0 0    rgba(18, 124, 22, 0.5); }
      70%  { box-shadow: 0 0 0 14px rgba(18, 124, 22, 0);   }
      100% { box-shadow: 0 0 0 0    rgba(18, 124, 22, 0);   }
    }
  `;
  document.head.appendChild(style);
};

export const runOnboardingTour = (steps: OnboardingStep[]) => {
  const visibleSteps = steps.filter((step) => step.show);
  if (visibleSteps.length === 0) return;

  injectTourThemeStyle();

  const isDarkMode = document.documentElement.getAttribute('data-joy-color-scheme') === 'dark';
  const overlayColor = isDarkMode ? brand.blur.light : brand.blur.dark;
  const overlayOpacity = isDarkMode ? 0.2 : 0.1;

  let popoverRoot: Root | null = null;
  let customContainer: HTMLDivElement | null = null;
  let interactiveTarget: HTMLElement | null = null;
  let interactiveHandler: ((event: Event) => void) | null = null;

  let lastShownIdx = -1;
  const stepEventProps = (idx: number) => {
    const step = visibleSteps[idx];
    return {
      platform_page: TUTORIAL_PLATFORM_PAGE,
      feature_name: TUTORIAL_FEATURE_NAME,
      tutorial_step_number: `Step ${idx + 1}`,
      tutorial_screen_header_text: tourHeaderText(step?.title),
    };
  };

  const detachInteractiveListener = () => {
    if (interactiveTarget && interactiveHandler) {
      interactiveTarget.removeEventListener('click', interactiveHandler);
    }
    interactiveTarget = null;
    interactiveHandler = null;
  };

  const attachInteractiveListener = (idx: number) => {
    detachInteractiveListener();
    const step = visibleSteps[idx];
    if (!step?.interactive) return;
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return;
    const handler = () => {
      if (idx === visibleSteps.length - 1) tour.destroy();
      else tour.moveNext();
    };
    target.addEventListener('click', handler);
    interactiveTarget = target;
    interactiveHandler = handler;
  };

  const layout = document.getElementById('layout');
  const originalLayoutOverflow = layout?.style.overflow ?? '';
  if (layout) layout.style.overflow = 'hidden';

  const mountReact = (popover: { wrapper: HTMLElement }, idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return;

    const wrapper = popover.wrapper;
    wrapper.style.padding = '0';
    wrapper.style.maxWidth = 'none';
    wrapper.style.width = 'auto';
    wrapper.style.background = 'transparent';
    wrapper.style.boxShadow = 'none';
    wrapper.style.borderRadius = '10px';
    Array.from(wrapper.children).forEach((child) => {
      if (child === customContainer) return;
      const el = child as HTMLElement;
      if (el.classList.contains('driver-popover-arrow')) return;
      el.style.display = 'none';
    });

    const arrow = wrapper.querySelector<HTMLDivElement>('.driver-popover-arrow');
    if (arrow && !arrow.querySelector('.calyx-tour-dot')) {
      const dot = document.createElement('div');
      dot.className = 'calyx-tour-dot';
      arrow.appendChild(dot);
    }

    if (customContainer && customContainer.parentNode !== wrapper) {
      wrapper.appendChild(customContainer);
    }
    if (!customContainer) {
      customContainer = document.createElement('div');
      wrapper.appendChild(customContainer);
      popoverRoot = createRoot(customContainer);
    }

    flushSync(() => {
      popoverRoot?.render(
        createElement(
          CssVarsProvider,
          { theme, defaultMode: 'light' },
          createElement(HomeOnboardingPopover, {
            title: step.title,
            body: step.body,
            customContent: step.customContent,
            stepIndex: idx,
            totalSteps: visibleSteps.length,
            interactive: step.interactive,
            onBack: () => {
              track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text: 'Back',
              });
              if (idx > 0) tour.movePrevious();
            },
            onContinue: () => {
              track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text: idx === visibleSteps.length - 1 ? 'Finish' : 'Continue',
              });
              if (idx === visibleSteps.length - 1) tour.destroy();
              else tour.moveNext();
            },
            onClose: () => {
              track(EVNT_tutorial_screen_closed, EVENT_TUTORIAL, stepEventProps(idx));
              tour.destroy();
            },
          })
        )
      );
    });
  };

  const hasInteractiveStep = visibleSteps.some((step) => step.interactive);

  const tour = driver({
    showProgress: false,
    showButtons: [],
    allowClose: !hasInteractiveStep,
    smoothScroll: false,
    overlayColor,
    overlayOpacity,
    stagePadding: 0,
    popoverOffset: 20,
    popoverClass: 'driverjs-onboarding-theme',
    onDestroyed: () => {
      detachInteractiveListener();
      if (layout) layout.style.overflow = originalLayoutOverflow;
      const root = popoverRoot;
      popoverRoot = null;
      customContainer = null;
      if (root) queueMicrotask(() => root.unmount());
    },
    onPopoverRender: (popover, { state }) => {
      const idx = state.activeIndex ?? 0;
      // PGV once per step (onPopoverRender may fire on re-render).
      if (idx !== lastShownIdx) {
        lastShownIdx = idx;
        track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, stepEventProps(idx));
      }
      mountReact(popover, idx);
      attachInteractiveListener(idx);
    },
    steps: visibleSteps.map((s) => ({
      element: s.element,
      popover: { side: s.side, align: s.align ?? 'center', title: '', description: '' },
    })),
  });

  tour.drive();
};

export const useOnboardingTour = (steps: OnboardingStep[]) => {
  const canShow = hasVisibleOnboardingSteps(steps);
  const start = useCallback(() => runOnboardingTour(steps), [steps]);
  return { canShow, start };
};
